import styled from "@emotion/styled"
import { PHeading, PText } from "@porsche-design-system/components-react"
import type { PTextProps } from "@porsche-design-system/components-react"
import {
	spacingStaticXs,
	spacingStaticSm,
	spacingStaticMd,
	spacingStaticLg,
	spacingStaticXl,
	spacingFluidSm,
} from "@porsche-design-system/components-react/emotion"
import type { PropsWithChildren, ReactElement, ReactNode } from "react"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { NewBadge } from "src/components/badge/NewBadge"

interface ContainerHeaderProps extends PropsWithChildren {
	actions?: ReactElement<any> | ReactElement<any>[]
	/** shown directly below the title and the subtitle, unlike children, which are separated from them by a larger gap */
	belowTitle?: ReactNode
	className?: string
	isBelowBackNavigation?: boolean
	isNew?: boolean
	subtitle?: string
	subtitleColor?: PTextProps["color"]
	title: string
}

const Heading = ({ title }: { title: string }) => (
	<PHeading tag="h1" size="2xl">
		{title}
	</PHeading>
)

export const ContainerHeader = ({
	actions,
	belowTitle,
	children,
	className,
	isBelowBackNavigation,
	isNew,
	subtitle,
	subtitleColor,
	title,
}: ContainerHeaderProps) => (
	<Header
		className={className}
		$isBelowBackNavigation={isBelowBackNavigation}
		$hasActions={!!actions}
		$hasChildren={!!children}
	>
		<TitleColumn>
			{isNew ? (
				<TitleWrapper>
					<Heading title={title} />
					{isNew && <NewBadge data-testid="container-header-new-badge-id" />}
				</TitleWrapper>
			) : (
				<Heading title={title} />
			)}
			{subtitle && <PText color={subtitleColor}>{subtitle}</PText>}
			{belowTitle && <div>{belowTitle}</div>}
		</TitleColumn>
		{actions && <ActionsWrapper>{actions}</ActionsWrapper>}
		{children && <ChildrenWrapper>{children}</ChildrenWrapper>}
	</Header>
)

const Header = styled("header", { shouldForwardProp })<{
	$hasActions?: boolean
	$hasChildren?: boolean
	$isBelowBackNavigation?: boolean
}>`
	display: grid;
	grid-template-columns: ${({ $hasActions }) => ($hasActions ? "1fr auto" : "1fr")};
	gap: ${spacingStaticLg};
	padding: ${({ $hasChildren, $isBelowBackNavigation }) =>
		`${$isBelowBackNavigation ? spacingStaticMd : spacingStaticXl} 0 ${$hasChildren ? spacingStaticMd : spacingStaticLg}`};
`

const TitleColumn = styled.div`
	display: grid;
	gap: ${spacingStaticXs};
`

const TitleWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: ${spacingStaticSm};
`

const ActionsWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: ${spacingFluidSm};
`

const ChildrenWrapper = styled.div`
	display: grid;
	grid-column: 1 / -1;
`
